import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { buildCompanyPath } from "../../utils/landingCompany";
import Logo from "../../components/logo/Logo";
import costsFirstLogo from "../../images/costsfirst-logo.webp";
import "./DemoEnded.css";

// Reached after a guest ("G") user logs out or their session times out —
// see AppLayout's handleLogout/handleIdleTimeout. Deliberately does NOT
// auto-redirect anywhere: this is the best conversion moment there is
// (they just watched the product work), so it offers real next steps
// instead of dropping a guest onto a login form they have no credentials
// for.
const MARKETING_SITE_URL = "https://costsfirst.com"; // TODO: confirm exact domain (costsfirst.com vs turningpoint.com)
const FEEDBACK_EMAIL = "support@costsfirst.com"; // TODO: confirm real inbox
// Full URL (not a relative navigate) so the ?ref=costsfirst param is present
// on load — isDemoHost() requires it in production to show DemoLanding
// instead of the real login form (see landingCompany.js).
const NEW_DEMO_URL = "https://demo.costsfirst.com/?ref=costsfirst";

export default function DemoEnded() {
  const theme = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Router state covers an in-app navigate() (idle timeout, manual logout).
  // The query param covers a hard window.location.href redirect, which is
  // all a plain axios response interceptor can do — see api/axios.js's
  // 401/403 handler for an actually-expired guest token.
  const reason =
    location.state?.reason ||
    new URLSearchParams(location.search).get("reason") ||
    undefined;
  const headingRef = useRef(null);

  // Same reasoning as DemoLanding: this page is arrived at via a full
  // navigation (post-logout), so give screen-reader users an immediate,
  // unambiguous announcement of what's now on screen.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="demo-ended-outer">
      <div className="demo-ended-card">
        <div className="demo-ended-logo-row">
          <img
            src={costsFirstLogo}
            alt={theme?.main_company_name || "CostsFirst"}
            className="demo-ended-logo"
          />
        </div>

        <span className="demo-ended-badge">SESSION ENDED · NOTHING SAVED</span>

        <p className="demo-ended-heading" ref={headingRef} tabIndex={-1}>
          {reason === "timeout" ? (
            "Your demo session has ended"
          ) : (
            <>
              Thanks for trying{" "}
              <span className="demo-ended-heading-accent">CostsFirst!</span>
            </>
          )}
        </p>
        <p className="demo-ended-subtext">
          You just saw how fast and easy it is to build a professional net
          sheet. Create a free account to save your work, or start another
          demo with a different address.
        </p>

        <div className="demo-ended-actions">
          <button
            type="button"
            className="demo-ended-primary-btn"
            onClick={() => navigate(buildCompanyPath("register"))}
          >
            Create a account
          </button>
          <button
            type="button"
            className="demo-ended-secondary-btn"
            onClick={() => {
              window.location.href = NEW_DEMO_URL;
            }}
          >
            Start a new demo
          </button>
        </div>

        <div className="demo-ended-quiet-links">
          <a
            href={`mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(
              "CostsFirst demo feedback",
            )}`}
            className="demo-ended-quiet-link"
          >
            Tell us what you thought →
          </a>
          <a href={MARKETING_SITE_URL} className="demo-ended-quiet-link">
            Return to costsfirst.com
          </a>
        </div>
      </div>

      <div className="company-logo top-15">
        <Logo type="secondary" size={140} />
      </div>
    </div>
  );
}
